import { formatDateTime, type Locale, useLocale, useTranslation } from '@multaqa/i18n'
import { ListSkeleton } from '@multaqa/ui/components/async/list-skeleton'
import { Badge } from '@multaqa/ui/components/ui/badge'
import { Button } from '@multaqa/ui/components/ui/button'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@multaqa/ui/components/ui/table'
import { Link, useParams } from '@tanstack/react-router'
import { BanIcon } from 'lucide-react'
import { InlineQueryError } from '@/components/inline-query-error'
import { localizedText } from '@/lib/localized-text'
import type { GroupCompany, SupportSession } from './api'
import { useGroupSupportSessions, useRevokeSupportSession } from './api'

/**
 * Sections shared by the group detail screen (plans/58 D-17), extracted to
 * keep that screen under the 300-line rule: the group's companies, and its
 * support sessions with revoke on the live ones. The cross-group overview
 * lives on the Support access screen; these are the per-group answers an
 * operator needs while standing on the group itself.
 */

export function sessionState(session: SupportSession): 'active' | 'expired' | 'revoked' {
  if (session.revoked_at !== null) return 'revoked'
  return new Date(session.expires_at).getTime() > Date.now() ? 'active' : 'expired'
}

export function GroupCompanies({
  companies,
  locale,
}: {
  companies: GroupCompany[]
  locale: Locale
}) {
  const { t } = useTranslation()
  const { groupId } = useParams({ from: '/_authenticated/groups/$groupId/' })
  return (
    <section data-testid="group-companies-panel" aria-labelledby="group-companies-heading">
      <h2 id="group-companies-heading" className="text-h3 mb-3">
        {t('groups.detail.companies.title', { count: companies.length })}
      </h2>
      {companies.length === 0 ? (
        <EmptyState
          title={t('groups.detail.companies.emptyTitle')}
          description={t('groups.detail.companies.emptyBody')}
          className="border border-border py-8"
        />
      ) : (
        <ul
          className="border border-border divide-y divide-border"
          data-testid="group-companies-list"
        >
          {companies.map((company) => (
            <li key={company.id}>
              <Link
                to="/groups/$groupId/companies/$companyId"
                params={{ groupId, companyId: company.id }}
                className="flex cursor-pointer items-center justify-between gap-3 px-4 py-2 transition-ui hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
                data-testid={`group-company-link-${company.id}`}
              >
                <span className="text-body">{localizedText(company.name, locale)}</span>
                <span className="text-identifier font-mono text-muted-foreground text-small">
                  {company.id.slice(0, 8)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export function GroupSupportSessions({ groupId }: { groupId: string }) {
  const { t } = useTranslation()
  const sessions = useGroupSupportSessions(groupId)
  const revoke = useRevokeSupportSession()
  const list = sessions.data ?? []

  return (
    <section data-testid="group-sessions-panel" aria-labelledby="group-sessions-heading">
      <h2 id="group-sessions-heading" className="text-h3 mb-3">
        {t('groups.detail.sessions.title')}
      </h2>
      {sessions.isLoading ? (
        <ListSkeleton columns={[1.25, 1.25, 2, 1, 0.75]} rows={3} />
      ) : sessions.isError ? (
        <InlineQueryError
          title={t('error.asyncTitle')}
          // The server's own message — never a generic failure line.
          description={sessions.error.message}
          retryLabel={t('action.retry')}
          onRetry={() => void sessions.refetch()}
        />
      ) : list.length === 0 ? (
        <EmptyState
          title={t('groups.detail.sessions.emptyTitle')}
          description={t('groups.detail.sessions.emptyBody')}
          className="border border-border py-8"
        />
      ) : (
        <Table className="border border-border">
          <TableHeader>
            <TableRow className="bg-muted hover:bg-muted">
              <TableHead>{t('supportSessions.sessions.columns.granted')}</TableHead>
              <TableHead>{t('supportSessions.sessions.columns.expires')}</TableHead>
              <TableHead>{t('supportSessions.sessions.columns.reason')}</TableHead>
              <TableHead>{t('supportSessions.sessions.columns.status')}</TableHead>
              <TableHead className="text-end">
                {t('supportSessions.sessions.columns.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                revoking={revoke.isPending}
                onRevoke={() => revoke.mutate(session.id)}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  )
}

function SessionRow({
  session,
  revoking,
  onRevoke,
}: {
  session: SupportSession
  revoking: boolean
  onRevoke: () => void
}) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const state = sessionState(session)

  return (
    <TableRow data-testid={`group-session-${session.id}`}>
      <TableCell className="text-identifier font-mono tabular-nums whitespace-nowrap">
        {formatDateTime(new Date(session.granted_at), locale)}
      </TableCell>
      <TableCell className="text-identifier font-mono tabular-nums whitespace-nowrap">
        {formatDateTime(new Date(session.expires_at), locale)}
      </TableCell>
      <TableCell className="text-body">
        {session.reason}
        {session.channel === 'mcp' ? (
          <p className="text-small text-muted-foreground">
            {t('supportSessions.sessions.viaAssistant')}
          </p>
        ) : null}
      </TableCell>
      <TableCell>
        <Badge tone={state === 'active' ? 'success' : 'neutral'}>
          {t(`supportSessions.sessions.${state}`)}
        </Badge>
      </TableCell>
      <TableCell className="text-end">
        {state === 'active' ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            loading={revoking}
            disabled={revoking}
            onClick={onRevoke}
            data-testid={`group-session-revoke-${session.id}`}
            aria-label={t('supportSessions.sessions.revoke')}
          >
            <BanIcon className="text-danger size-3.5" aria-hidden="true" />
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  )
}
